import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { capitalize, isUndefined, orderBy, sortBy } from 'lodash';
import { Icons } from '@bbkAdminUtils/icons';
import { cs } from '@bbkAdminUtils/css-utils';
import {
  type DefaultActionButton,
  GenericPopover,
  type ModalOnCloseReason,
} from '@bbkAdminComponents/popovers/generic-popover';
import { ButtonV2 } from '@bbkAdminComponents/buttons';
import {
  Checkbox,
  CheckboxLabel,
} from '@bbkAdminComponents/switches/checkbox/checkbox-v2';
import {
  useSelectAllFunnels,
  useSelectAllFunnelsEntities,
  useSelectFunnelsByBrandIdOrAll,
  useSelectFunnelsLoading,
} from '@bbkAdminRedux/rtkq/funnels.slice';
import { selectAppId } from '@bbkAdminRedux/app/selectors';
import { NavIcons } from '@bbkAdminComponents/app-layout/nav';
import {
  appPersistSlice,
  useGetDemoSuffix,
} from '@bbkAdminRedux/rtkq/app-persist/app-persist.slice';
import {
  type AudienceFilter,
  type DeliveryFilter,
  type ExperienceFilter,
  ReportsFilterType,
  selectDeliveryReportFilter,
  selectExperiencesAudiencesReportFilter,
  selectReportFilterForPicker,
} from '@bbkAdminRedux/rtkq/app-persist/reports-filter';
import { useAppDispatch } from '@bbkAdminRedux/redux_utils';
import {
  rulesSlice,
  useSelectAllRulesEntities,
} from '@bbkAdminRedux/rtkq/rules.slice';
import TimePicker from '@bbkAdminComponents/popovers/time-picker';
import {
  type TimePickerStructure,
  useSelectTimePeriods,
} from '@bbkAdminUtils/time-periods';
import {
  datepickerPersistSlice,
  useSelectDatePicker,
} from '@bbkAdminRedux/rtkq/datepicker-persist.slice';
import {
  type FunnelAudience,
  useSelectFunnelAudiencesHistory,
  useSelectFunnelAudiencesHistoryByBrandId,
} from '@bbkAdminRedux/rtkq/reports.slice';
import { type ReactFCC } from '@bbkAdminUtils/utility-types';
import { isDefined } from '@bbkAdminRedux/rtkq/rtkq-utils';
import { BBKRequestContext } from '@bbkAdminRedux/rtkq/bbkApi.slice';
import { FeatureFlagsE, useFeatureFlags } from '@bbkAdminUtils/feature-flags';
import { usePlans } from '@bbkAdminUtils/plans';
import {
  type BrandOptionType,
  DEFAULT_BRAND_ID,
  useGetBrandOptions,
} from '@bbkAdminRedux/rtkq/brands.slice';
import {
  GenericSelect,
  type SelectOption,
} from '@bbkAdminComponents/select/select';
import { GlobalLogger } from '@bbkAdminUtils/GlobalLogger';
import {
  BrandControl,
  BrandDisplayName,
} from '@bbkAdminComponents/experiences/branding/brand-dropdown';
import { OfferPresentation } from '@bbkAdminUtils/api-client/rest-offers-api';
import { useShowDemoData } from '@bbkAdminComponents/play/play-context';
import { SButton, SIcon } from '@chargebee/sting-react';

import '@bbkAdminComponents/reports-filter-picker/reports-filter-picker.scss';

const checkmark = (
  <svg
    width="15"
    height="15"
    viewBox="0 0 15 15"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M10.7768 4.1394L7.14968 10.1488L4.45724 7.39925C4.3374 7.27745 4.16212 7.29845 4.063 7.44895C3.96557 7.59875 3.98293 7.81924 4.10276 7.94174L7.02032 10.9209C7.07072 10.972 7.134 11 7.19784 11C7.20904 11 7.22024 10.9993 7.23144 10.9979C7.30704 10.986 7.37592 10.937 7.42184 10.8614L11.2242 4.56149C11.3171 4.40679 11.2925 4.1877 11.1687 4.0715C11.0461 3.9539 10.8703 3.9847 10.7768 4.1394Z"
      stroke="white"
    />
  </svg>
);

const minus = (
  <svg
    width="15"
    height="15"
    viewBox="0 0 15 15"
    xmlns="http://www.w3.org/2000/svg"
    style={{ fill: 'white' }}
  >
    <path d="M11.5 7H3.5C3.22386 7 3 7.22386 3 7.5C3 7.77614 3.22386 8 3.5 8H11.5C11.7761 8 12 7.77614 12 7.5C12 7.22386 11.7761 7 11.5 7Z" />
  </svg>
);

type ReportCheckboxProps = {
  checked?: boolean;
  partiallyChecked?: boolean;
  onChange?: React.ComponentProps<typeof Checkbox>['onChange'];
};
const ReportCheckbox: ReactFCC<ReportCheckboxProps> = ({
  checked,
  partiallyChecked,
  onChange,
  children,
}) => {
  const bold = !!(checked || partiallyChecked);
  return (
    <CheckboxLabel
      className={cs('tw-select-none tw-text-sm tw-leading-7 tw-transition', {
        'tw-text-black': bold,
        'tw-text-black/50': !bold,
      })}
      css={css`
        > div {
          margin-right: 0.25rem;
        }
      `}
    >
      <Checkbox
        size="tiny"
        checked={bold}
        icon={partiallyChecked ? minus : checkmark}
        onChange={onChange}
      />
      {children}
    </CheckboxLabel>
  );
};

export const Separator = styled.div`
  border-top: 1px solid rgba(0, 0, 0, 0.1);
`;

const ReportsFilterContext = createContext<{
  filterType: ReportsFilterType;
  setFilterType: React.Dispatch<React.SetStateAction<ReportsFilterType>>;

  brandFilter: string | undefined;
  setBrandFilter: React.Dispatch<React.SetStateAction<string | undefined>>;

  experiencesFilters: ExperienceFilter[];
  setExperiencesFilters: React.Dispatch<
    React.SetStateAction<ExperienceFilter[]>
  >;

  audiencesFilters: AudienceFilter[];
  setAudiencesFilters: React.Dispatch<React.SetStateAction<AudienceFilter[]>>;

  deliveryFilters: Set<DeliveryFilter>;
  setDeliveryFilters: React.Dispatch<React.SetStateAction<Set<DeliveryFilter>>>;

  timeRange: TimePickerStructure;
  setTimeRange: React.Dispatch<React.SetStateAction<TimePickerStructure>>;

  lastBrandFilter: string | undefined;
  setLastBrandFilter: React.Dispatch<React.SetStateAction<string | undefined>>;

  save: () => void;
  reset: () => void;
}>(undefined!);

const ReportsFilterProvider: React.FC<{ children: React.ReactElement }> = ({
  children,
}) => {
  const dispatch = useAppDispatch();
  const suffix = useGetDemoSuffix();
  const isDemoData = useShowDemoData();
  const reportsFilter = useSelector(selectReportFilterForPicker(suffix));
  const [filterType, setFilterType] = useState(reportsFilter.type);
  const [brandFilter, setBrandFilter] = useState(reportsFilter.brandFilter);
  const [lastBrandFilter, setLastBrandFilter] = useState(
    reportsFilter.lastBrandFilter
  );

  const isBrandFilterEnabled = useFeatureFlags(
    FeatureFlagsE.ADDITIONAL_BRANDS_ENABLED
  );
  const isPredictiveEnabled = useFeatureFlags(FeatureFlagsE.ENABLE_PREDICTIVE);
  useEffect(() => {
    if (isPredictiveEnabled && brandFilter !== reportsFilter.brandFilter) {
      setBrandFilter(reportsFilter.brandFilter);
    }
  }, [reportsFilter.brandFilter]);

  useEffect(() => {
    if (!isBrandFilterEnabled && isDefined(brandFilter)) {
      setBrandFilter(undefined);
    }
  }, [isBrandFilterEnabled, brandFilter]);

  const [experiencesFilters, setExperiencesFilters] = useState(
    reportsFilter.experiencesFilters ?? []
  );
  const [audiencesFilters, setAudiencesFilters] = useState(
    reportsFilter.audiencesFilters ?? []
  );
  const [deliveryFilters, setDeliveryFilters] = useState<Set<DeliveryFilter>>(
    new Set(
      reportsFilter.deliveryFilters ?? [
        OfferPresentation.modal,
        OfferPresentation.pop_up,
        OfferPresentation.banner,
        // OfferPresentation.card,
      ]
    )
  );
  const origTimeRange = useSelectDatePicker();
  const [timeRange, setTimeRangeState] =
    useState<TimePickerStructure>(origTimeRange);
  const setTimeRange: React.Dispatch<
    React.SetStateAction<TimePickerStructure>
  > = (timePicker) => {
    setTimeRangeState(timePicker);
  };

  const appKey = useSelector(selectAppId);
  const funnelsArgs = {
    appKey,
    context: isDemoData ? BBKRequestContext.reports_filter : undefined,
    brandId: brandFilter,
  };
  const brandFunnels = useSelectFunnelsByBrandIdOrAll(funnelsArgs);

  const reportArgs = {
    appKey,
    context: BBKRequestContext.reports_filter,
    brandId: brandFilter,
  };
  const { data: faHistory } =
    useSelectFunnelAudiencesHistoryByBrandId(reportArgs);

  const allFASelected = audiencesFilters.length === faHistory.ranges.length;

  const allExperiencesSelected =
    experiencesFilters.length === brandFunnels.length;

  const buildExperienceFilters = (): ExperienceFilter[] => {
    const experienceFiltersBuilt: ExperienceFilter[] = brandFunnels
      .filter((f) => f.id !== undefined)
      .map((f) => ({
        field: 'funnel',
        operator: 'equals',
        value: f.id,
      }));
    setExperiencesFilters(experienceFiltersBuilt);
    return experienceFiltersBuilt;
  };

  const initExperiencesFilters = (): void => {
    if (reportsFilter.experiencesFilters !== undefined) return;
    buildExperienceFilters();
  };

  useEffect(() => {
    initExperiencesFilters();
  }, [reportsFilter.experiencesFilters, brandFunnels]);

  const buildAudiencesFilters = (): AudienceFilter[] => {
    const audiencesFiltersBuilt: AudienceFilter[] = faHistory.ranges.map(
      (r) => ({
        field: 'funnel_audiences',
        operator: 'equals',
        value: r.funnel_audiences,
      })
    );
    setAudiencesFilters(audiencesFiltersBuilt);
    return audiencesFiltersBuilt;
  };

  const initAudiencesFilters = (): void => {
    if (!faHistory) return;
    if (reportsFilter.audiencesFilters !== undefined) return;
    buildAudiencesFilters();
  };

  useEffect(() => {
    initAudiencesFilters();
  }, [reportsFilter.audiencesFilters, faHistory]);

  const generateExperienceFilters = (): ExperienceFilter[] | undefined => {
    if (
      (allExperiencesSelected || experiencesFilters.length === 0) &&
      brandFilter === undefined
    ) {
      buildExperienceFilters();
      return undefined;
    }
    if (allExperiencesSelected || experiencesFilters.length === 0) {
      return buildExperienceFilters();
    }

    return experiencesFilters;
  };

  const generateAudiencesFilters = (): AudienceFilter[] | undefined => {
    if (
      (allFASelected || audiencesFilters.length === 0) &&
      brandFilter === undefined
    ) {
      buildAudiencesFilters();
      return undefined;
    }
    if (allFASelected || audiencesFilters.length === 0) {
      return buildAudiencesFilters();
    }
    return audiencesFilters;
  };

  const save = (): void => {
    dispatch(
      appPersistSlice.actions.saveReportsFilter({
        appKey,
        suffix,
        type: filterType,
        brandFilter,
        experiencesFilters: generateExperienceFilters(),
        audiencesFilters: generateAudiencesFilters(),
        deliveryFilters: Array.from(deliveryFilters),
      })
    );

    dispatch(
      datepickerPersistSlice.actions.saveDatepicker({
        appKey,
        suffix,
        ...timeRange,
      })
    );
  };

  const reset = (): void => {
    setFilterType(reportsFilter.type);
    setBrandFilter(reportsFilter.brandFilter);
    initExperiencesFilters();
    initAudiencesFilters();
    setTimeRange(origTimeRange);
  };

  return (
    <ReportsFilterContext.Provider
      value={{
        filterType,
        setFilterType,

        brandFilter,
        setBrandFilter,

        experiencesFilters,
        setExperiencesFilters,

        audiencesFilters,
        setAudiencesFilters,

        deliveryFilters,
        setDeliveryFilters,

        timeRange,
        setTimeRange,

        lastBrandFilter,
        setLastBrandFilter,
        save,
        reset,
      }}
    >
      {children}
    </ReportsFilterContext.Provider>
  );
};

const BrandingFilter: React.FC<{
  isPredictiveEnabled?: boolean | undefined;
}> = ({ isPredictiveEnabled }) => {
  const {
    brandFilter,
    setBrandFilter,
    setExperiencesFilters,
    setAudiencesFilters,
    lastBrandFilter,
    setLastBrandFilter,
  } = useContext(ReportsFilterContext);
  const appKey = useSelector(selectAppId);
  const isDemoData = useShowDemoData();

  const funnelsArgs = {
    appKey,
    context: isDemoData ? BBKRequestContext.reports_filter : undefined,
  };
  const allFunnels = useSelectAllFunnels(funnelsArgs);

  const defaultChoice = {
    value: 'all',
    label: 'Show all',
  };

  const brandOptions = useGetBrandOptions({ appKey });

  const [brandList, setBrandList] = useState<BrandOptionType[]>([
    defaultChoice,
  ]);
  const dispatch = useAppDispatch();
  const suffix = useGetDemoSuffix();
  const reportsFilter = useSelector(selectReportFilterForPicker(suffix));
  useEffect(() => {
    if (!brandOptions) return;
    const brandOptionsWithAll = [defaultChoice, ...brandOptions];

    setBrandList(brandOptionsWithAll);
  }, [brandOptions]);

  const reportArgs = {
    appKey,
    context: BBKRequestContext.reports_filter,
  };
  const { data: faHistory } = useSelectFunnelAudiencesHistory(reportArgs);

  const getFilteredFunnels = (brandId: string | undefined) => {
    let filteredFunnels = allFunnels.filter((f) => f.id !== undefined);
    if (brandId !== 'all' && brandId !== DEFAULT_BRAND_ID) {
      filteredFunnels = filteredFunnels.filter((f) => f.brand_id === brandId);
    } else if (brandId === DEFAULT_BRAND_ID) {
      filteredFunnels = filteredFunnels.filter((f) => isUndefined(f.brand_id));
    }
    return filteredFunnels;
  };

  const buildExperienceFilters = (
    brandId: string | undefined
  ): ExperienceFilter[] => {
    if (brandId === undefined) {
      return [];
    }
    const filteredFunnels = getFilteredFunnels(brandId);
    return filteredFunnels.map((f) => ({
      field: 'funnel',
      operator: 'equals',
      value: f.id,
    }));
  };

  const buildAudienceFilters = (
    brandId: string | undefined
  ): AudienceFilter[] => {
    if (brandId === undefined) {
      return [];
    }
    const filteredFunnels = getFilteredFunnels(brandId);
    return faHistory.ranges
      .filter((r) => {
        const [fId] = r.funnel_audiences.split(',');
        return filteredFunnels.find((f) => f.id === Number(fId));
      })
      .map(
        (r) =>
          ({
            field: 'funnel_audiences',
            operator: 'equals',
            value: r.funnel_audiences,
          }) as AudienceFilter
      );
  };

  if (isPredictiveEnabled) {
    const isAllBrandsSelected = !brandFilter && !!lastBrandFilter;
    const handleChange = () => {
      setLastBrandFilter(brandFilter);
      const updatedAllSelectedBrand = !isAllBrandsSelected;
      const updatedBrandFilter = updatedAllSelectedBrand
        ? undefined
        : lastBrandFilter;
      const updatedExperienceFilters =
        buildExperienceFilters(updatedBrandFilter);
      const updatedAudienceFilters = buildAudienceFilters(updatedBrandFilter);
      setBrandFilter(updatedBrandFilter);
      setExperiencesFilters(updatedExperienceFilters);
      setAudiencesFilters(updatedAudienceFilters);
      // FIXME: need to use save() from the context instead of custom dispatch
      // At the moment there is race condition between setAudiencesFilters and save()
      // that causes save() to save old values of the filters
      dispatch(
        appPersistSlice.actions.saveReportsFilter({
          appKey,
          suffix,
          type: reportsFilter.type,
          brandFilter: updatedBrandFilter,
          lastBrandFilter: brandFilter,
          experiencesFilters: updatedExperienceFilters,
          audiencesFilters: updatedAudienceFilters,
        })
      );
    };

    return (
      <div className="tw-flex tw-items-center">
        <label className="tw-mr-2 tw-flex tw-items-center tw-pt-2">
          <input
            className="tw-m-1"
            type="checkbox"
            checked={isAllBrandsSelected}
            onChange={handleChange}
          />
          View all brands
        </label>
        <GenericSelect
          short
          isSearchable={false}
          components={{ Control: BrandControl }}
          options={brandOptions}
          value={
            brandFilter
              ? brandList.find((b) => b.value === (brandFilter ?? 'all'))
              : defaultChoice
          }
          className={cs('tw-z-20', {
            'tw-pointer-events-none tw-opacity-50':
              isAllBrandsSelected === true,
          })}
          onChange={(option: SelectOption<string>) => {
            setLastBrandFilter(brandFilter);
            const brandID = option.value === 'all' ? undefined : option.value;
            setBrandFilter(brandID);
            const updatedExperienceFilters = buildExperienceFilters(brandID);
            const updatedAudienceFilters = buildAudienceFilters(brandID);
            setExperiencesFilters(updatedExperienceFilters);
            setAudiencesFilters(updatedAudienceFilters);
            // FIXME: need to use save() from the context instead of custom dispatch
            dispatch(
              appPersistSlice.actions.saveReportsFilter({
                appKey,
                suffix,
                type: reportsFilter.type,
                brandFilter: brandID,
                lastBrandFilter,
                experiencesFilters: updatedExperienceFilters,
                audiencesFilters: updatedAudienceFilters,
              })
            );
          }}
          formatOptionLabel={(option) => {
            if (option.value === 'all') {
              const lastBrand = brandList.find(
                (b) => b.value === lastBrandFilter
              );

              return (
                <BrandDisplayName
                  label={lastBrand ? lastBrand.label : 'All brands'}
                  is_default={
                    lastBrand ? lastBrand.value === DEFAULT_BRAND_ID : false
                  }
                  targeting_enabled={
                    lastBrand ? lastBrand.data?.targeting_enabled : undefined
                  }
                />
              );
            }

            return (
              <BrandDisplayName
                label={option.label}
                is_default={option.value === DEFAULT_BRAND_ID}
                targeting_enabled={option.data?.targeting_enabled}
              />
            );
          }}
        />
      </div>
    );
  }
  return (
    <>
      <GenericSelect
        isSearchable
        value={
          brandFilter
            ? brandList.find((b) => b.value === (brandFilter ?? 'all'))
            : defaultChoice
        }
        onChange={(option: SelectOption<string>) => {
          setBrandFilter(option.value === 'all' ? undefined : option.value);
          setExperiencesFilters(buildExperienceFilters(option.value));
          setAudiencesFilters(buildAudienceFilters(option.value));
        }}
        placeholder="Choose brand"
        options={brandList}
      />
    </>
  );
};

const ExperiencesFilter: React.FC = () => {
  const { experiencesFilters, setExperiencesFilters } =
    useContext(ReportsFilterContext);

  const appKey = useSelector(selectAppId);
  const isDemoData = useShowDemoData();

  const funnelsArgs = {
    appKey,
    context: isDemoData ? BBKRequestContext.reports_filter : undefined,
  };
  const { isLoading } = useSelectFunnelsLoading(funnelsArgs);
  const brandFunnels = useSelectFunnelsByBrandIdOrAll(funnelsArgs);
  const sortedFunnels = useMemo(
    () =>
      orderBy(
        brandFunnels,
        [(f) => f.is_control, (f) => !f.active, (f) => f.display_name],
        ['desc']
      ),
    [brandFunnels]
  );
  const testFunnels = useMemo(
    () => sortedFunnels.filter((f) => !f.is_control),
    [sortedFunnels]
  );

  const everyoneElse = (): React.ReactNode => {
    const testFunnelsIds = testFunnels.map((f) => f.id);
    const selectedFunnels = testFunnels.filter((f) => {
      if (f.is_control) return false;
      return experiencesFilters.find((paf) => paf.value === f.id);
    });
    const someSelected = selectedFunnels.length !== 0;
    const allSelected = selectedFunnels.length === sortedFunnels.length - 1;
    return (
      <ReportCheckbox
        partiallyChecked={!allSelected && someSelected}
        checked={allSelected}
        onChange={(e) => {
          const nonTestFilters =
            experiencesFilters.filter(
              (paf) => !testFunnelsIds.includes(paf.value)
            ) ?? [];
          if (e.target.checked) {
            setExperiencesFilters([
              ...nonTestFilters,
              ...testFunnels.map((f) => ({
                field: 'funnel' as const,
                operator: 'equals' as const,
                value: f.id,
              })),
            ]);
          } else {
            setExperiencesFilters(nonTestFilters);
          }
        }}
      >
        <span className={cs({ 'tw-font-medium': false })}>
          {NavIcons.audiences} Fallback
        </span>
      </ReportCheckbox>
    );
  };

  if (isLoading) return <>Loading...</>;
  if (!experiencesFilters) {
    GlobalLogger.error('filters are undefined');
    return <>Error happened, try to refresh page</>;
  }

  let archivedLabelRendered = false;
  return (
    <>
      {sortedFunnels.map((f) => {
        const filterIdx = experiencesFilters.findIndex(
          (paf) => paf.value === f.id
        );
        const checked = filterIdx !== -1;
        const renderArchiveLabel = (): React.ReactNode => {
          if (archivedLabelRendered) return null;
          archivedLabelRendered = true;
          return (
            <span className="tw-text-xs tw-uppercase tw-text-black/30">
              Archived
            </span>
          );
        };
        return (
          <div key={f.id} className={cs({ 'tw-ml-5': !f.is_control })}>
            {!f.active && renderArchiveLabel()}
            <ReportCheckbox
              checked={checked}
              onChange={(e) => {
                if (e.target.checked) {
                  setExperiencesFilters([
                    ...experiencesFilters,
                    {
                      field: 'funnel',
                      operator: 'equals',
                      value: f.id,
                    },
                  ]);
                } else {
                  setExperiencesFilters([
                    ...experiencesFilters.slice(0, filterIdx),
                    ...experiencesFilters.slice(filterIdx + 1),
                  ]);
                }
              }}
            >
              <span
                className={cs('tw-truncate', { 'tw-font-medium': checked })}
                title={f.display_name}
              >
                {f.is_control ? NavIcons.holdoutPage : NavIcons.pages}{' '}
                {f.display_name}
              </span>
            </ReportCheckbox>
            {f.is_control ? everyoneElse() : null}
          </div>
        );
      })}
    </>
  );
};

const AudiencesFilter: React.FC = () => {
  const { audiencesFilters, setAudiencesFilters, brandFilter } =
    useContext(ReportsFilterContext);

  const appKey = useSelector(selectAppId);
  const isDemoData = useShowDemoData();

  const reportArgs = {
    appKey,
    context: BBKRequestContext.reports_filter,
    brandId: brandFilter,
  };
  const { data: report, isFetching: isAudiencesHistoryFetching } =
    useSelectFunnelAudiencesHistoryByBrandId(reportArgs);

  const rulesArgs = {
    appKey,
    context: BBKRequestContext.reports_filter,
  };
  const { isFetching: areRulesFetching } =
    rulesSlice.endpoints.getRules.useQuery(rulesArgs);
  const allRulesMap = useSelectAllRulesEntities(rulesArgs);

  const funnelsArgs = {
    appKey,
    context: isDemoData ? BBKRequestContext.reports_filter : undefined,
  };
  const { isFetching: areFunnelsFetching } =
    useSelectFunnelsLoading(funnelsArgs);
  const allFunnelsMap = useSelectAllFunnelsEntities(funnelsArgs);

  const isLoading =
    areRulesFetching || isAudiencesHistoryFetching || areFunnelsFetching;

  const flatHistory = useMemo(() => {
    if (isLoading) return [];
    return sortBy(
      (report.ranges ?? []).map((r) => r.funnel_audiences).filter(isDefined),
      (fa) => {
        const [fId, ...group] = fa.split(',');
        const funnelName = allFunnelsMap[fId!]?.display_name ?? '~unknown';

        const names = group.map((g) => {
          if (g === 'control') return '!control';
          if (g === 'fallback') return '~1fallback';
          if (g === 'other') return '~2other';
          if (g === '') return '~3legacy';
          return allRulesMap[g]?.display_name ?? '~4unknown';
        });

        return [...names, names.length * -1, funnelName].join();
      },
      'desc'
    );
  }, [isLoading, allFunnelsMap, allRulesMap, report]);

  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  const isCollapsableGroup = (groupKeyStr: string): boolean =>
    collapsed[groupKeyStr] !== undefined;

  useEffect(() => {
    setCollapsed(
      Object.fromEntries(
        flatHistory.map((fa) => [fa.split(',')[1], true] as [string, boolean])
      )
    );
  }, [flatHistory]);

  const getGroupFAs = (group: string[]): FunnelAudience[] =>
    flatHistory.filter(
      (f) =>
        f
          .split(',')
          .slice(1, group.length + 1)
          .join(',') === group.join(',')
    );

  const onChangeGroup = (group: string[], checked: boolean): void => {
    const toProcess = getGroupFAs(group);
    const otherAudienceFilters = audiencesFilters.filter(
      (af) => !toProcess.includes(af.value)
    );

    if (checked) {
      setAudiencesFilters([
        ...otherAudienceFilters,
        ...toProcess.map(
          (fa) =>
            ({
              field: 'funnel_audiences',
              operator: 'equals',
              value: fa,
            }) as AudienceFilter
        ),
      ]);
    } else {
      setAudiencesFilters(otherAudienceFilters);
    }
  };

  const lastRenderedGroup: string[] = [];

  const isCollapsed = (groupKey: string): boolean =>
    Boolean(collapsed[groupKey]);

  const renderNumberPages = (numPages: number): string =>
    `${numPages} ${numPages === 1 ? 'page' : 'pages'}`;

  const renderFA = (fa: FunnelAudience): React.ReactNode => {
    const [fId, ...groups] = fa.split(',');
    const funnel = allFunnelsMap[fId!];
    if (!funnel) return null;
    const filterIdx = audiencesFilters.findIndex((paf) => paf.value === fa);
    const checked = filterIdx !== -1;
    const topGroup = groups[0]!;
    return (
      <div key={fa}>
        {groups.map((g, idx, arr) => {
          if (lastRenderedGroup[idx] === g) return null;
          lastRenderedGroup[idx] = g;
          const title =
            g === 'control'
              ? 'Control'
              : g === 'fallback'
                ? 'Fallback'
                : g === 'other'
                  ? 'Other'
                  : g === ''
                    ? 'Legacy'
                    : (allRulesMap[g]?.display_name ?? 'Unknown');
          const groupKey = arr.slice(0, idx + 1);
          const groupKeyStr = groupKey.join(',');
          const groupFAs = getGroupFAs(groupKey);
          const selectedGroupFAs = groupFAs.filter((f) =>
            audiencesFilters.find((af) => af.value === f)
          );
          const someSelected = selectedGroupFAs.length !== 0;
          const allSelected = selectedGroupFAs.length === groupFAs.length;
          const isGroupCollapsed =
            groupKeyStr !== topGroup && isCollapsed(topGroup);
          return (
            <div
              key={`${fa}-${g}`}
              className={cs('tw-overflow-hidden tw-transition-all', {
                'tw-ml-5': idx === 1,
                'tw-ml-10': idx === 2,
                'tw-flex tw-justify-between': isCollapsableGroup(groupKeyStr),
                'tw-h-0': isGroupCollapsed,
                'tw-pb-px': !isGroupCollapsed, // for animation
              })}
            >
              <div className="tw-overflow-hidden">
                <ReportCheckbox
                  partiallyChecked={!allSelected && someSelected}
                  checked={allSelected}
                  onChange={(e) => onChangeGroup(groupKey, e.target.checked)}
                >
                  <span
                    className={cs('tw-truncate', { 'tw-font-medium': checked })}
                    title={title}
                  >
                    {g === 'control'
                      ? NavIcons.holdoutPage
                      : NavIcons.audiences}{' '}
                    {title}
                  </span>
                </ReportCheckbox>
              </div>
              {isCollapsableGroup(groupKeyStr) && (
                <div
                  className="tw-flex tw-cursor-pointer tw-select-none tw-items-center"
                  onClick={() =>
                    setCollapsed({
                      ...collapsed,
                      [groupKeyStr]: !collapsed[groupKeyStr],
                    })
                  }
                >
                  <span className="tw-mr-2 tw-whitespace-nowrap tw-text-xs tw-text-black/50">
                    {renderNumberPages(groupFAs.length)}
                  </span>
                  <span className="tw-text-cbrprimary-600">
                    <div
                      className={cs('tw-inline-block tw-transition-all', {
                        'tw--scale-y-100': !collapsed[topGroup],
                      })}
                    >
                      {Icons.chevronDownSmall}
                    </div>
                  </span>
                </div>
              )}
            </div>
          );
        })}
        <div
          className={cs('tw-overflow-hidden tw-transition-all', {
            'tw-ml-5': groups.length === 1,
            'tw-ml-10': groups.length === 2,
            'tw--ml-1 tw-pl-16': groups.length === 3,
            'tw-h-0': isCollapsed(topGroup),
            'tw-pb-px': !isCollapsed(topGroup),
          })}
        >
          <ReportCheckbox
            checked={checked}
            onChange={(e) => {
              if (e.target.checked) {
                setAudiencesFilters([
                  ...audiencesFilters,
                  {
                    field: 'funnel_audiences',
                    operator: 'equals',
                    value: fa,
                  },
                ]);
              } else {
                setAudiencesFilters([
                  ...audiencesFilters.slice(0, filterIdx),
                  ...audiencesFilters.slice(filterIdx + 1),
                ]);
              }
            }}
          >
            <span
              className={cs('tw-truncate', { 'tw-font-medium': checked })}
              title={funnel.display_name}
            >
              {NavIcons.pages} {funnel.display_name}
            </span>
          </ReportCheckbox>
        </div>
      </div>
    );
  };

  if (isLoading) return <>Loading...</>;
  if (!audiencesFilters) {
    GlobalLogger.error('filters are undefined');
    return <>Error happened, try to refresh page</>;
  }
  return <>{flatHistory.map((fa) => renderFA(fa))}</>;
};

const DateFilter: React.FC<{ isLCOReportsFilter?: boolean }> = ({
  isLCOReportsFilter,
}) => {
  const isDemoData = useShowDemoData();
  const { timeRange, setTimeRange } = useContext(ReportsFilterContext);
  const { isStarterFreePlan } = usePlans();
  const showAllTimeRanges = !isStarterFreePlan;

  const timeValues = useSelectTimePeriods();

  return (
    <div
      css={css`
        .time-picker-v2 {
          display: block;

          .timeAgoButton {
            display: flex;
            justify-content: space-between;
            border: 1px solid rgba(0, 0, 0, 10%);
            color: black;
            padding: 0.375rem 0.375rem 0.375rem 1rem;

            > span {
              display: flex;
              align-items: center;

              > svg {
                display: none;
              }
            }
          }

          .bbk-date-picker {
            display: flex;
            justify-content: space-between;
            flex-wrap: wrap;
            gap: 0.5rem;
            margin-top: 0.5rem;

            .bbk-date-picker-label {
              flex: 1;
              padding: 0.375rem 0.375rem 0.375rem 1rem;
              border: 1px solid rgba(0, 0, 0, 10%);
              border-radius: 0.25rem;
            }

            .bbk-date-picker-label-text {
              display: block;
            }

            .flatpickr-input {
              width: 100%;
              color: black;
              height: auto;

              &:before {
                display: block;
                content: 'Start date';
              }
            }

            .flatpickr-input#flatpickr-first {
              text-align: left;
            }

            > span {
              display: none;
            }
          }
        }
      `}
    >
      <TimePicker
        className="time-picker-v2"
        save={setTimeRange}
        readOnly={!showAllTimeRanges}
        disableButton={isDemoData && !isLCOReportsFilter}
        timePicker={timeRange}
        timeValues={timeValues}
      />
    </div>
  );
};

const deliveryFilterOptions = [
  {
    label: 'Modal',
    value: OfferPresentation.modal,
  },
  {
    label: 'Pop-up',
    value: OfferPresentation.pop_up,
  },
  {
    label: 'Banner',
    value: OfferPresentation.banner,
  },
];
type ReportsFilterProps = {
  isLCOReportsFilter?: boolean;
  showDeliveryFilter?: boolean;
  disabled?: boolean;
};
const ReportsFilter: React.FC<ReportsFilterProps> = ({
  isLCOReportsFilter,
  showDeliveryFilter,
  disabled,
}) => {
  const { isStarterFreePlan, isStarterPremiumPlan } = usePlans();
  const isStarterPlan = isStarterFreePlan || isStarterPremiumPlan;
  const showExtendedFilters = !isStarterPlan;
  const isDemoData = useShowDemoData();

  const isBrandFilterEnabled = useFeatureFlags(
    FeatureFlagsE.ADDITIONAL_BRANDS_ENABLED
  );
  const isPredictiveEnabled = useFeatureFlags(FeatureFlagsE.ENABLE_PREDICTIVE);

  const {
    filterType,
    setFilterType,

    brandFilter,

    experiencesFilters,
    setExperiencesFilters,

    audiencesFilters,
    setAudiencesFilters,

    deliveryFilters,
    setDeliveryFilters,

    save,
    reset,
  } = useContext(ReportsFilterContext);

  const suffix = useGetDemoSuffix();
  const { type: appliedFilterType } = useSelector(
    selectReportFilterForPicker(suffix)
  );
  const appliedFilters = useSelector(
    selectExperiencesAudiencesReportFilter(suffix)
  );
  const appliedDeliveryFilters = useSelector(
    selectDeliveryReportFilter(suffix)
  );
  const appliedFiltersLength = appliedFilters.length;

  const handlePopoverClose = (reason?: ModalOnCloseReason): void => {
    if (reason) setTimeout(() => reset(), 500);
  };

  const appliedDeliveryFiltersLength = appliedDeliveryFilters.length;
  const actionButton = useMemo(
    () => (localProps: React.ComponentProps<typeof DefaultActionButton>) => {
      return (
        <button
          className="timeAgoButton"
          type="button"
          {...localProps}
          disabled={disabled}
        >
          <span className="tw-ml-1 tw-mr-2 tw-inline-block tw-self-baseline">
            {Icons.funnel}
          </span>
          {isLCOReportsFilter ? (
            showDeliveryFilter ? (
              <>
                <span>Filters</span>
                <div
                  className={cs(
                    'tw-ml-1 tw-flex tw-px-2 tw-py-1 tw-text-xs tw-font-bold',
                    'tw-rounded-full tw-border tw-border-cbrprimary-500 tw-bg-cbrprimary-50 tw-text-cbrprimary-700'
                  )}
                >
                  Delivery (
                  {appliedDeliveryFiltersLength ===
                    deliveryFilterOptions.length ||
                  appliedDeliveryFiltersLength === 0
                    ? 'all'
                    : appliedDeliveryFiltersLength}
                  )
                </div>
              </>
            ) : (
              <span>Date</span>
            )
          ) : (
            <>
              Filters{' '}
              <div className="tw-ml-1 tw-flex tw-rounded-full tw-bg-cbrprimary-50 tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-text-cbrprimary-600">
                {appliedFilterType === ReportsFilterType.experiences
                  ? 'Experiences '
                  : 'Audiences '}
                ({appliedFiltersLength === 0 ? 'all' : appliedFiltersLength})
              </div>
            </>
          )}
          {Icons.doubleChevron}
        </button>
      );
    },
    [
      appliedFilterType,
      appliedFiltersLength,
      appliedDeliveryFiltersLength,
      disabled,
    ]
  );

  const appKey = useSelector(selectAppId);
  const reportArgs = {
    appKey,
    context: BBKRequestContext.reports_filter,
    brandId: brandFilter,
  };
  const { data: faHistoryReport } =
    useSelectFunnelAudiencesHistoryByBrandId(reportArgs);
  const funnelsArgs = {
    appKey,
    context: isDemoData ? BBKRequestContext.reports_filter : undefined,
    brandId: brandFilter,
  };
  const brandFunnels = useSelectFunnelsByBrandIdOrAll(funnelsArgs);

  const allFASelected =
    audiencesFilters.length === faHistoryReport.ranges.length;
  const someFASelected = audiencesFilters.length !== 0;

  const allExperiencesSelected =
    experiencesFilters.length === brandFunnels.length &&
    brandFilter === undefined;
  const someExperiencesSelected = experiencesFilters.length !== 0;

  const allSelected =
    filterType === ReportsFilterType.experiences
      ? allExperiencesSelected
      : allFASelected;
  const someSelected =
    filterType === ReportsFilterType.experiences
      ? someExperiencesSelected
      : someFASelected;
  const partiallyChecked = !allSelected && someSelected;

  return (
    <GenericPopover
      popoverProps={{
        PaperProps: {
          style: { width: '20rem', overflow: 'initial' },
        },
        classes: { paper: 'bbk-generic-dialog-popover' },
        disableEnforceFocus: true,
      }}
      actionButton={actionButton}
      onClose={handlePopoverClose}
    >
      {({ handleClose }) => {
        const handleSave = (): void => {
          save();
          handleClose();
        };

        const handleCancel = (): void => {
          reset();
          handleClose();
        };

        return (
          <div
            css={css`
              svg {
                fill: rgba(0, 0, 0, 0.5);
              }
            `}
          >
            <div className="bbk-gdialog-header tw-px-5 tw-py-4">
              <div className="bbk-gdialog-title">Filters</div>
              <div className="bbk-gdialog-header-actions">
                <ButtonV2
                  secondary
                  className="bbk-gdialog-button tw-mr-2"
                  onClick={handleCancel}
                >
                  Cancel
                </ButtonV2>
                <ButtonV2
                  primary
                  className="bbk-gdialog-button"
                  onClick={handleSave}
                >
                  Apply
                </ButtonV2>
              </div>
            </div>
            <Separator />
            <div className="tw-my-3 tw-px-6">
              <div className="tw-mb-2 tw-text-sm tw-text-cbrneutral-500">
                Date filter
              </div>
              <DateFilter isLCOReportsFilter={isLCOReportsFilter} />
            </div>
            {isLCOReportsFilter ? (
              showDeliveryFilter ? (
                <>
                  <Separator />
                  <div className="tw-my-4 tw-px-6">
                    <div className="tw-mb-2 tw-text-sm tw-text-cbrneutral-500">
                      Filter by
                    </div>
                    <GenericSelect
                      options={[
                        {
                          label: 'Delivery',
                          value: ReportsFilterType.delivery,
                        },
                      ]}
                      value={{
                        label: 'Delivery',
                        value: ReportsFilterType.delivery,
                      }}
                      onChange={() => setFilterType(ReportsFilterType.delivery)}
                    />
                    <div className="tw-pt-4">
                      <ReportCheckbox
                        checked={
                          deliveryFilters.size === deliveryFilterOptions.length
                        }
                        partiallyChecked={
                          deliveryFilters.size > 0 &&
                          deliveryFilters.size < deliveryFilterOptions.length
                        }
                        onChange={() => {
                          setDeliveryFilters(
                            new Set(deliveryFilterOptions.map((x) => x.value))
                          );
                        }}
                      >
                        Show all
                      </ReportCheckbox>
                      <div className="tw-ml-4">
                        {deliveryFilterOptions.map((option) => (
                          <ReportCheckbox
                            key={option.value}
                            checked={deliveryFilters.has(option.value)}
                            onChange={() =>
                              setDeliveryFilters((prev) => {
                                const newFilters = new Set(prev);
                                if (newFilters.has(option.value)) {
                                  newFilters.delete(option.value);
                                } else {
                                  newFilters.add(option.value);
                                }
                                return newFilters;
                              })
                            }
                          >
                            {option.label}
                          </ReportCheckbox>
                        ))}
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <></>
              )
            ) : (
              <>
                {isBrandFilterEnabled && !isPredictiveEnabled && (
                  <>
                    <Separator />
                    <div className="tw-my-3 tw-px-6">
                      <div className="tw-mb-2 tw-text-sm tw-text-black/50">
                        Brand filter
                      </div>
                      <BrandingFilter />
                    </div>
                  </>
                )}
                {showExtendedFilters && (
                  <>
                    <Separator />
                    <div className="tw-my-2 tw-px-6">
                      <ReportCheckbox
                        checked={allSelected}
                        partiallyChecked={partiallyChecked}
                        onChange={(e) => {
                          const checked = e.target.checked;

                          if (filterType === ReportsFilterType.experiences) {
                            if (checked) {
                              setExperiencesFilters(
                                brandFunnels.map((funnel) => ({
                                  field: 'funnel',
                                  operator: 'equals',
                                  value: funnel.id,
                                }))
                              );
                            } else {
                              setExperiencesFilters([]);
                            }
                          } else if (checked) {
                            setAudiencesFilters(
                              faHistoryReport.ranges.map((fa) => ({
                                field: 'funnel_audiences',
                                operator: 'equals',
                                value: fa.funnel_audiences,
                              }))
                            );
                          } else {
                            setAudiencesFilters([]);
                          }
                        }}
                      >
                        <div className="!tw-mr-0 tw-flex tw-w-full tw-items-center tw-justify-between">
                          <span className="tw-mx-1 tw-font-medium">
                            Filter by
                          </span>
                          <div
                            className="tw-flex tw-flex-1 tw-overflow-clip tw-rounded-3xl tw-border tw-border-cbrneutral-200 tw-text-center tw-text-xs"
                            onClick={(e) => e.preventDefault()}
                          >
                            <div
                              className={cs(
                                'tw-w-1/2 tw-rounded-2xl tw-py-1.5 tw-duration-500',
                                {
                                  'tw-bg-cbrprimary-50 tw-font-medium tw-text-cbrprimary-600':
                                    filterType ===
                                    ReportsFilterType.experiences,
                                }
                              )}
                              onClick={() =>
                                setFilterType(ReportsFilterType.experiences)
                              }
                            >
                              Experiences
                            </div>
                            <div
                              className={cs(
                                'tw-w-1/2 tw-rounded-2xl tw-py-1.5 tw-duration-500',
                                {
                                  'tw-bg-cbrprimary-50 tw-font-medium tw-text-cbrprimary-600':
                                    filterType === ReportsFilterType.audiences,
                                }
                              )}
                              onClick={() =>
                                setFilterType(ReportsFilterType.audiences)
                              }
                            >
                              Audiences
                            </div>
                          </div>
                        </div>
                      </ReportCheckbox>
                    </div>
                    <div className="tw-max-h-96 tw-overflow-y-auto tw-px-6 tw-pb-3 tw-transition-all">
                      {filterType === ReportsFilterType.experiences && (
                        <ExperiencesFilter />
                      )}
                      {filterType === ReportsFilterType.audiences && (
                        <AudiencesFilter />
                      )}
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        );
      }}
    </GenericPopover>
  );
};

const DatePickerV2ActionButton: React.FC<
  React.ComponentProps<typeof DefaultActionButton>
> = ({ onClick, disabled }) => {
  const { timeRange } = useContext(ReportsFilterContext);
  const timeValues = useSelectTimePeriods();

  const timeAgoText = timeValues.find(
    (r) => r.value === timeRange.timeAgoKey
  )?.name;

  return (
    <div className="tw-flex tw-items-center tw-gap-2 tw-whitespace-nowrap">
      <SIcon name="calendar" />
      <button
        className="tw-flex tw-w-36 tw-items-center tw-justify-between tw-rounded-md tw-border tw-border-cbrneutral-200 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-text-cbrneutral-900 tw-transition-colors hover:tw-border-cbrneutral-300 hover:tw-bg-cbrneutral-50 disabled:tw-cursor-not-allowed disabled:tw-opacity-50"
        type="button"
        onClick={onClick}
        disabled={disabled}
      >
        <span className="tw-truncate">
          {timeAgoText ? capitalize(timeAgoText) : 'Pick a range'}
        </span>
        <SIcon
          className="tw-ml-2 tw-h-4 tw-w-4 tw-flex-shrink-0"
          name="chevron-down"
        />
      </button>
    </div>
  );
};

export const DatePickerV2Inner: React.FC = () => {
  const { save, reset } = useContext(ReportsFilterContext);
  const isDemoData = useShowDemoData();

  const handlePopoverClose = (reason?: ModalOnCloseReason): void => {
    if (reason) setTimeout(() => reset(), 500);
  };

  const actionButton = useMemo(
    () => (localProps: React.ComponentProps<typeof DefaultActionButton>) => (
      <DatePickerV2ActionButton {...localProps} disabled={isDemoData} />
    ),
    []
  );

  return (
    <GenericPopover
      popoverProps={{
        PaperProps: {
          style: { width: '20rem', overflow: 'initial', zIndex: 1300 },
        },
        classes: { paper: 'bbk-generic-dialog-popover' },
        disableEnforceFocus: true,
        disableRestoreFocus: true,
      }}
      actionButton={actionButton}
      onClose={handlePopoverClose}
    >
      {({ handleClose }) => {
        const handleSave = (): void => {
          save();
          handleClose();
        };

        const handleCancel = (): void => {
          reset();
          handleClose();
        };

        return (
          <div className="tw-flex tw-w-full tw-flex-col tw-py-4">
            <div className="tw-flex tw-items-center tw-justify-between tw-px-6 tw-pb-4">
              <span className="tw-block tw-text-heading-md tw-text-cbrneutral-900">
                Date filter
              </span>
              <div>
                <SButton
                  onClick={handleCancel}
                  variant="neutral-outline"
                  size="regular"
                  disabled={isDemoData}
                >
                  Cancel
                </SButton>
                <SButton
                  onClick={handleSave}
                  variant="primary"
                  size="regular"
                  className="tw-ml-2"
                  disabled={isDemoData}
                >
                  Apply
                </SButton>
              </div>
            </div>
            <Separator />
            <span className="tw-px-6 tw-pt-4 tw-text-xs tw-font-medium">
              Date
            </span>
            <div className="tw-px-6 tw-pb-4 tw-pt-2">
              <DateFilter />
            </div>
          </div>
        );
      }}
    </GenericPopover>
  );
};

export const DatePickerV2: React.FC = () => {
  return (
    <ReportsFilterProvider>
      <div className="tw-flex tw-items-center tw-gap-2">
        <DatePickerV2Inner />
        <TimePicker hidePicker readOnly />
      </div>
    </ReportsFilterProvider>
  );
};

export const ReportsFilterPicker: React.FC<ReportsFilterProps> = (props) => (
  <ReportsFilterProvider>
    <>
      <ReportsFilter {...props} />
      <TimePicker hidePicker readOnly />
    </>
  </ReportsFilterProvider>
);

export const PredictiveBrandFilterPicker: React.FC = () => (
  <ReportsFilterProvider>
    <>
      <BrandingFilter isPredictiveEnabled />
    </>
  </ReportsFilterProvider>
);
